import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiMail,
  FiLock,
  FiLogIn,
  FiEye,
  FiEyeOff,
  FiAlertCircle,
  FiShield,
} from "react-icons/fi";
import { MdSchool } from "react-icons/md";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { roleHome } from "../utils/roles";

const Login = () => {
  const [form, setForm] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSiteSettings();
  const from = location.state?.from?.pathname;

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError("Please enter both email and password");
      return;
    }
    setLoading(true);
    try {
      const data = await login(form.email, form.password);
      toast.success("Login successful! Welcome back.");
      // Redirect to the dashboard for the user's role (or back to where they came from)
      navigate(from || roleHome(data?.user?.role), { replace: true });
    } catch (err) {
      const msg =
        err.response?.data?.message || "Invalid credentials. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1e3a8a] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background decoration circles */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-600/20 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-sky-400/20 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl" />
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-indigo-500/10 rounded-full blur-2xl" />

      {/* Top accent line */}
      <div className="absolute left-0 top-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-sky-300 to-blue-500" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="px-10 pt-10 pb-8 text-center border-b border-blue-50" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #1D4ED8 100%)' }}>
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/30">
              <MdSchool className="w-9 h-9 text-white" />
            </div>
            <h1 className="text-xl font-bold text-white tracking-wide mb-2">
              {settings?.schoolName || "Hillbert English Boarding School "}
            </h1>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 border border-white/30 rounded-full">
              <FiShield className="w-3 h-3 text-sky-200" />
              <span className="text-sky-100 text-xs font-semibold uppercase tracking-widest">Admin Portal</span>
            </div>
          </div>

          {/* Form */}
          <div className="px-10 py-8">
            <h2 className="text-2xl font-bold text-[#1e3a8a] mb-6">
              Welcome <span className="text-[#2563EB]">back</span>
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300 w-4 h-4" />
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="admin@school.edu.np"
                    className="w-full pl-11 pr-4 py-3.5 bg-blue-50/50 border border-blue-100 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">
                  Password
                </label>
                <div className="relative">
                  <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300 w-4 h-4" />
                  <input
                    name="password"
                    type={showPass ? "text" : "password"}
                    value={form.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="w-full pl-11 pr-12 py-3.5 bg-blue-50/50 border border-blue-100 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-blue-300 hover:text-blue-500 transition-colors"
                  >
                    {showPass ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2.5 text-red-600 text-sm bg-red-50 border border-red-200 px-4 py-3 rounded-xl"
                >
                  <FiAlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                  {error}
                </motion.div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-4 font-bold text-white text-sm hover:-translate-y-0.5 transition-all duration-200 rounded-xl shadow-lg hover:shadow-xl disabled:opacity-70 disabled:hover:translate-y-0 mt-2"
                style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #2563EB 100%)' }}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <FiLogIn className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center pt-6 border-t border-blue-50">
              <Link
                to="/"
                className="text-blue-400 hover:text-[#1D4ED8] text-sm font-medium transition-colors inline-flex items-center gap-1.5"
              >
                &larr; Back to Website
              </Link>
            </div>
          </div>
        </div>

        <p className="text-center text-blue-200/70 text-xs mt-6">
          &copy; {new Date().getFullYear()} {settings?.schoolName || "Hillbert English Boarding School "}. All rights reserved.
        </p>
      </motion.div>
    </div>
  );
};

export default Login;
